import { Box, Tab, TabList, Tabs } from "@chakra-ui/react";
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { DEVICEPARAMS, PERIODS } from "../consts";
import { Period } from "../types";
import DatePicker, { DateObject } from "react-multi-date-picker";
import RangePicker from "./RangePicker";
import { useQueryParams } from "../hooks/useQueryParams";
import dayjs from "dayjs";

interface Props {
  period?: string;
  from?: string;
  to?: string;
}

export const PeriodTabStrip = (props: Props) => {
  const data = PERIODS;
  const [searchParams, setSearchParams] = useSearchParams();
  const [queryParams] = useQueryParams(DEVICEPARAMS);
  const locationId = queryParams.id;

  const isCustom = props.period === "custom";

  const initialCustomDates = useMemo(() => {
    if (isCustom && props.from && props.to) {
      return [
        new DateObject({ date: props.from, format: "YYYY-MM-DD" }),
        new DateObject({ date: props.to, format: "YYYY-MM-DD" }),
      ] as DateObject[];
    }
    return undefined;
  }, [isCustom, props.from, props.to]);

  const [customDates, setCustomDates] = useState<DateObject[] | undefined>(initialCustomDates);

  useEffect(() => {
    setCustomDates(initialCustomDates);
  }, [initialCustomDates]);

  const derivedTabIndex = useMemo(() => {
    if (isCustom) return null;
    const idx = data.findIndex((p) => p.period === props.period);
    return idx >= 0 ? idx : 0;
  }, [data, isCustom, props.period]);

  const [tabIndex, setTabIndex] = useState<number>(derivedTabIndex);
  useEffect(() => setTabIndex(derivedTabIndex), [derivedTabIndex]);

  useEffect(() => {
    const hasFrom = searchParams.has("from");
    const hasTo = searchParams.has("to");

    if (!hasFrom || !hasTo) {
      const newParams = Object.fromEntries(searchParams.entries());

      if (isCustom && props.from && props.to) {
        newParams.from = dayjs(props.from).format("YYYY-MM-DD");
        newParams.to = dayjs(props.to).format("YYYY-MM-DD");
        newParams.period = "custom";
      } else {
        newParams.from = dayjs().startOf("day").format("YYYY-MM-DD");
        newParams.to = dayjs().format("YYYY-MM-DD");
      }

      setSearchParams(newParams, { replace: true });
    }
  }, [isCustom, props.from, props.to, searchParams, setSearchParams]);

  useEffect(() => {
    if (!isCustom) setCustomDates(undefined);
  }, [locationId, isCustom]);

  const handleTabsChange = (index: number) => setTabIndex(index);

  const handleNavigation = (tabItem: Period, idx: number) => {
    setCustomDates(undefined);
    setTabIndex(idx);
    setSearchParams(
      {
        ...queryParams,
        from: tabItem.from,
        to: tabItem.to,
        period: tabItem.period || "custom",
        last_hour: "false",
      },
      { replace: true }
    );
  };

  const tabItems = data.map((tabItem, idx) => (
    <Tab
      key={tabItem.title}
      fontSize="13px"
      borderRadius="full"
      bg={tabIndex === idx ? "#484848" : "#F5F6F7"}
      color={tabIndex === idx ? "white" : "#484848"}
      _hover={{ bg: tabIndex === idx ? "gray.700" : "gray.200" }}
      px="3"
      py=".2rem"
      mr=".1rem"
      fontWeight={tabIndex === idx ? 700 : 400}
      fontFamily="Inter"
      onClick={() => handleNavigation(tabItem, idx)}
    >
      {tabItem.title}
    </Tab>
  ));

  return (
    <Tabs variant="unstyled" index={tabIndex} onChange={handleTabsChange}>
      <TabList gap={2}>
        {tabItems}
        <Box key="custom-date" width="max-content" alignSelf="center" ml={1} cursor="pointer">
          <DatePicker
            className="gray"
            value={customDates}
            showOtherDays
            onChange={(dateObjects: DateObject[]) => {
              if (dateObjects?.length === 2) {
                const [start, end] = dateObjects;
                setCustomDates(dateObjects);
                setTabIndex(null)
                setSearchParams(
                  {
                    ...queryParams,
                    from: start.format("YYYY-MM-DD"),
                    to: end.format("YYYY-MM-DD"),
                    period: "custom",
                    last_hour: "false",
                  },
                  { replace: true }
                );
              }
            }}
            range
            render={<RangePicker />}
            mapDays={({ date }) => {
              const isToday = date.format("YYYY-MM-DD") === dayjs().format("YYYY-MM-DD");
              return {
                style: isToday
                  ? { backgroundColor: "#3182ce", color: "white", borderRadius: "50%" }
                  : {},
              };
            }}
          />
        </Box>
      </TabList>
    </Tabs>
  );
};
